import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Star } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { SITE } from "@/data/site";
import { useLiveStats } from "@/lib/liveStats";

const navLinks = [
  { to: "/reviews", label: "Reviews", id: NAV.linkReviews, internal: true },
  { to: "/#packages", label: "Packages", id: NAV.linkPackages },
  { to: "/#towns", label: "Areas", id: NAV.linkTowns },
  { to: "/about", label: "About", id: "nav-link-about", internal: true },
  { to: "/#faq", label: "FAQ", id: NAV.linkFaq },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const stats = useLiveStats();

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-stone-50/80 border-b border-stone-200/70">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <span className="font-serif text-[1.5rem] md:text-[1.7rem] leading-none tracking-tight text-stone-900">
            Wedding Video Essex
            <span className="italic text-stone-500"> Reviews</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {navLinks.map((l) =>
            l.internal ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={l.id}
                className="text-[0.78rem] tracking-[0.18em] uppercase text-stone-700 hover:text-stone-900 transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.to}
                data-testid={l.id}
                className="text-[0.78rem] tracking-[0.18em] uppercase text-stone-700 hover:text-stone-900 transition-colors"
              >
                {l.label}
              </a>
            )
          )}
          <Link
            to="/reviews"
            data-testid="nav-rating-pill"
            className="flex items-center gap-1.5 text-[0.78rem] tracking-[0.05em] text-stone-600 hover:text-stone-900 transition-colors"
          >
            <Star className="w-3.5 h-3.5 fill-[#B8923A] text-[#B8923A]" />
            <span className="font-medium text-stone-900">{stats.rating.toFixed(1)}</span>
            <span className="font-serif italic">· {stats.total} reviews</span>
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={NAV.ctaBook}
            className="btn-primary text-[0.72rem] py-3 px-5"
          >
            Check My Date
          </a>
        </div>

        <button
          className="md:hidden p-2"
          onClick={() => setOpen(!open)}
          data-testid={NAV.toggle}
          aria-label="Menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-stone-50 px-6 py-6 space-y-4">
          {navLinks.map((l) =>
            l.internal ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`mobile-${l.id}`}
                onClick={() => setOpen(false)}
                className="block text-sm uppercase tracking-[0.18em] text-stone-700"
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.label}
                href={l.to}
                data-testid={`mobile-${l.id}`}
                onClick={() => setOpen(false)}
                className="block text-sm uppercase tracking-[0.18em] text-stone-700"
              >
                {l.label}
              </a>
            )
          )}
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary w-full"
            data-testid="mobile-nav-cta-book"
          >
            Check My Date
          </a>
        </div>
      )}
    </header>
  );
}
